import { gql } from '@apollo/client';

/**
 * OptiClock — support-agent time tracking.
 *
 * Every operation NAME matches its GraphQL field name, which is what makes
 * `refetchQueries: ['opticlockMyEntries']` resolve. Do not rename one without the other.
 */

const PROJECT_FIELDS = `
  _id
  name
  color
  archived
  sortOrder
  createdBy
  createdAt
  updatedBy
  updatedAt
`;

const ENTRY_FIELDS = `
  _id
  agentEmail
  projectId
  projectName
  projectColor
  entryDate
  minutes
  note
  createdAt
  createdBy
  updatedAt
  updatedBy
`;

const MONTH_FIELDS = `
  month
  agentEmail
  totalMinutes
  totalHours
  entries { ${ENTRY_FIELDS} }
`;

// ─────────────────────────────────── projects ──────────────────────────────────

/** The picker (includeArchived: false) and root's admin table (true) share one query. */
export const OPTICLOCK_PROJECTS = gql`
  query opticlockProjects($includeArchived: Boolean) {
    opticlockProjects(includeArchived: $includeArchived) { ${PROJECT_FIELDS} }
  }
`;

export const OPTICLOCK_UPSERT_PROJECT = gql`
  mutation opticlockUpsertProject($input: OpticlockProjectInput!) {
    opticlockUpsertProject(input: $input) { ${PROJECT_FIELDS} }
  }
`;

/** The only removal — there is no delete mutation, by design (entries FK to projects). */
export const OPTICLOCK_SET_PROJECT_ARCHIVED = gql`
  mutation opticlockSetProjectArchived($id: String!, $archived: Boolean!) {
    opticlockSetProjectArchived(id: $id, archived: $archived) { ${PROJECT_FIELDS} }
  }
`;

// ──────────────────────────────────── entries ──────────────────────────────────

/** The caller's own month. Takes no agentEmail — the server reads the token. */
export const OPTICLOCK_MY_ENTRIES = gql`
  query opticlockMyEntries($month: String!) {
    opticlockMyEntries(month: $month) { ${MONTH_FIELDS} }
  }
`;

/** Root reading any agent's month — backs the entry page's root-only agent selector. */
export const OPTICLOCK_AGENT_ENTRIES = gql`
  query opticlockAgentEntries($agentEmail: String!, $month: String!) {
    opticlockAgentEntries(agentEmail: $agentEmail, month: $month) { ${MONTH_FIELDS} }
  }
`;

export const OPTICLOCK_ADD_ENTRY = gql`
  mutation opticlockAddEntry($input: OpticlockEntryInput!) {
    opticlockAddEntry(input: $input) { ${ENTRY_FIELDS} }
  }
`;

export const OPTICLOCK_UPDATE_ENTRY = gql`
  mutation opticlockUpdateEntry($id: ID!, $patch: OpticlockEntryPatchInput!) {
    opticlockUpdateEntry(id: $id, patch: $patch) { ${ENTRY_FIELDS} }
  }
`;

export const OPTICLOCK_DELETE_ENTRY = gql`
  mutation opticlockDeleteEntry($id: ID!) {
    opticlockDeleteEntry(id: $id)
  }
`;

/**
 * Root correcting an entry it does not own. `agentEmail` CONFIRMS the row's owner — the server
 * matches on it and never reassigns ownership — so it must be the owner's email, not root's.
 */
export const OPTICLOCK_ADMIN_UPDATE_ENTRY = gql`
  mutation opticlockAdminUpdateEntry(
    $id: ID!
    $agentEmail: String!
    $patch: OpticlockEntryPatchInput!
  ) {
    opticlockAdminUpdateEntry(id: $id, agentEmail: $agentEmail, patch: $patch) { ${ENTRY_FIELDS} }
  }
`;

export const OPTICLOCK_ADMIN_DELETE_ENTRY = gql`
  mutation opticlockAdminDeleteEntry($id: ID!, $agentEmail: String!) {
    opticlockAdminDeleteEntry(id: $id, agentEmail: $agentEmail)
  }
`;

// ──────────────────────────────────── rollup ───────────────────────────────────

/** Root's Time Dashboard dashboard. Agents with no entries in the month are simply absent. */
export const OPTICLOCK_AGENT_MONTH_TOTALS = gql`
  query opticlockAgentMonthTotals($month: String!) {
    opticlockAgentMonthTotals(month: $month) {
      agentEmail
      minutes
      hours
      entryCount
      latestProjectId
      latestProjectName
      latestEntryDate
      latestCreatedAt
      byProject { projectId name minutes color }
    }
  }
`;
